/**
 * CouponsAnalytics — /admin/coupons/analytics
 *
 * Wave 5-Late Analytics PROPAGATION. Executive discipline inherited
 * BYTE-EXACT from the approved AnalyticsFunnel anchor:
 *   • AdminLayout wrap; body-top AdminPageHelp panel (moved out of
 *     the previously-broken `actions` slot).
 *   • Filter Section with `--ds-color-admin-border` hairline chrome,
 *     `--ds-radius-input` 3px on selects/inputs/buttons, dense
 *     `px-2.5 py-1.5`. CSV export uses the accent primary button.
 *   • KPI grid — 4 stat cards using the identical `Stat` primitive
 *     from the anchor: hairline chrome, `text-[26px] tabular-nums
 *     font-semibold` values; only the headline (Revenue impact) is
 *     highlighted with `--ds-color-accent-strong`.
 *   • Founding Member Section — restyled to `--ds-color-accent-soft`
 *     background + `--ds-color-accent` border (replaces raw
 *     rgba(183,148,244,…)), accent-strong progress bar on
 *     `--ds-color-surface-inset` track.
 *   • recharts palette — line stroke `--ds-color-accent-strong`,
 *     grid `--ds-color-admin-border`, axis ticks
 *     `--ds-color-ink-subtle`, **flat tooltip** (no boxShadow, per
 *     the anchor micro-fix).
 *   • Per-coupon table — dense `px-4 py-1.5` rows with
 *     `borderBottom: 1px solid var(--ds-color-admin-border)` + last-
 *     row guard, `overflow-x-auto` mobile wrapper, `tabular-nums
 *     font-mono` on all numeric columns, muted `Badge`-styled status
 *     chips with `--ds-radius-input` 3px.
 *
 * Business logic (range filter, coupon-code filter, blob CSV export,
 * founding-progress calculation) preserved BYTE-EXACT. All testids
 * unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  BarChart3,
  DollarSign,
  Download,
  Filter,
  Loader2,
  RefreshCw,
  Ticket,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import {
  LineChart as RCLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { api, formatApiError } from "@/api/client";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { AccentButton, GhostButton, Section, SelectInput, Stat, TextInput } from "@/admin/_primitives";

/* ------------------------------------------------------------------ */
/* Local admin primitives (mirror anchor).                              */
/* ------------------------------------------------------------------ */






function StatusChip({ status }) {
  // Status strings come from Stripe: active | expired | scheduled | disabled etc.
  // Map to muted admin tones. Fallback to neutral.
  const s = (status || "").toLowerCase();
  let color = "var(--ds-color-neutral-muted)";
  let bg = "var(--ds-color-neutral-muted-soft)";
  if (s === "active" || s === "live") {
    color = "var(--ds-color-success-muted)";
    bg = "var(--ds-color-success-muted-soft)";
  } else if (s === "expired" || s === "disabled") {
    color = "var(--ds-color-danger)";
    bg = "var(--ds-color-danger-soft)";
  } else if (s === "scheduled" || s === "pending") {
    color = "var(--ds-color-warning)";
    bg = "var(--ds-color-warning-soft)";
  }
  return (
    <span
      className="inline-block rounded-[var(--ds-radius-input)] px-2 py-[2px] text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{
        color,
        background: bg,
        border: `1px solid ${color}`,
      }}
    >
      {status || "—"}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* KPI Stat (same shape as anchor)                                      */
/* ------------------------------------------------------------------ */


/* ------------------------------------------------------------------ */
/* Utilities                                                            */
/* ------------------------------------------------------------------ */

function fmtDollars(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function CouponsAnalyticsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");
  const [couponCode, setCouponCode] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range };
      if (couponCode.trim()) params.coupon_code = couponCode.trim().toUpperCase();
      const { data } = await api.get("/admin/coupons/analytics", { params });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [range, couponCode]);

  useEffect(() => {
    load();
  }, [load]);

  async function exportCsv() {
    setExporting(true);
    try {
      const params = { range, export_csv: true };
      if (couponCode.trim()) params.coupon_code = couponCode.trim().toUpperCase();
      const res = await api.get("/admin/coupons/analytics", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `coupons-analytics-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  }

  const kpis = data?.kpis || {};
  const perCoupon = data?.per_coupon || [];
  const timeSeries = data?.time_series || [];
  const founding = data?.founding_progress;

  const filterBadge = (
    <div className="flex items-center gap-2">
      <GhostButton
        onClick={load}
        data-testid="admin-coupons-analytics-refresh"
        className="!py-1 !text-[11px]"
      >
        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
      </GhostButton>
      <AccentButton
        onClick={exportCsv}
        disabled={exporting}
        data-testid="admin-coupons-analytics-export"
      >
        {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
        CSV
      </AccentButton>
    </div>
  );

  return (
    <AdminLayout
      title="Coupon Analytics"
      subtitle="Redemption trends, revenue impact, and per-code performance."
      actions={
        <Link
          to="/admin/coupons"
          data-testid="admin-coupons-analytics-back-link"
          className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.14em]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to coupons
        </Link>
      }
    >
      <div data-testid="admin-coupons-analytics-page">
        <div className="mb-3 max-w-3xl">
          <AdminPageHelp
            title="Coupon Analytics"
            purpose="Redemption metrics, revenue impact, and conversion analysis per coupon code."
            configures="Nothing — read-only. Filter by coupon, time window, and cohort."
            impact="No user-facing impact. Diagnose which codes drive signups vs. discount abuse."
            safety="Read-only. Metrics update on schedule from raw Stripe events."
          />
        </div>

        <Section title="Filter" icon={Filter} badge={filterBadge} testid="admin-coupons-analytics-filters">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span
                className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Time window
              </span>
              <SelectInput
                data-testid="admin-coupons-analytics-range"
                value={range}
                onChange={(e) => setRange(e.target.value)}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="365d">Last 365 days</option>
                <option value="all">All time</option>
              </SelectInput>
            </label>
            <label className="block flex-1 min-w-[200px]">
              <span
                className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Filter code
              </span>
              <TextInput
                data-testid="admin-coupons-analytics-filter-code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Blank = all"
                className="w-full font-mono"
              />
            </label>
          </div>
        </Section>

        {loading && !data ? (
          <div
            className="text-center py-12 text-[13px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            <Loader2 className="w-4 h-4 animate-spin inline align-[-2px] mr-2" /> Loading analytics…
          </div>
        ) : (
          <>
            <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat
                testid="admin-coupons-analytics-kpi-redemptions"
                label={`Redemptions · ${range}`}
                value={kpis.total_redemptions ?? 0}
                icon={Ticket}
              />
              <Stat
                testid="admin-coupons-analytics-kpi-active-coupons"
                label="Active coupons"
                value={kpis.active_coupons ?? 0}
                icon={Zap}
              />
              <Stat
                testid="admin-coupons-analytics-kpi-revenue-impact"
                label={`Revenue impact · ${range}`}
                value={fmtDollars(kpis.total_revenue_impact_cents)}
                icon={DollarSign}
                highlight
              />
              <Stat
                testid="admin-coupons-analytics-kpi-trial-conversions"
                label="Trial conversions"
                value={kpis.trial_conversions ?? 0}
                icon={Users}
              />
            </div>

            {founding && (
              <Section
                title="Founding Member cohort"
                icon={Trophy}
                testid="admin-coupons-analytics-founding-progress-section"
                badge={
                  <span
                    className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    Code{" "}
                    <span className="font-mono" style={{ color: "var(--ds-color-ink)" }}>
                      {founding.code}
                    </span>
                  </span>
                }
                bodyClassName="p-4"
              >
                <div data-testid="admin-coupons-analytics-founding-progress">
                  <div className="flex items-baseline justify-between gap-4 mb-2 flex-wrap">
                    <div
                      className="text-[15px] font-semibold tabular-nums"
                      style={{ color: "var(--ds-color-ink)" }}
                    >
                      {founding.redemptions_count} of {founding.max_redemptions} slots claimed
                    </div>
                    <div
                      className="text-[11px] tabular-nums"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {founding.max_redemptions - founding.redemptions_count} slots remaining
                    </div>
                  </div>
                  <div
                    className="h-1.5 rounded-[var(--ds-radius-input)] overflow-hidden"
                    style={{ background: "var(--ds-color-surface-inset)" }}
                  >
                    <div
                      data-testid="admin-coupons-analytics-founding-progress-bar"
                      className="h-full transition-all duration-500"
                      style={{
                        width: `${Math.min(
                          100,
                          (founding.redemptions_count / Math.max(1, founding.max_redemptions)) * 100,
                        )}%`,
                        background: "var(--ds-color-accent-strong)",
                      }}
                    />
                  </div>
                </div>
              </Section>
            )}

            <Section
              title="Redemptions over time"
              icon={BarChart3}
              testid="admin-coupons-analytics-timeseries-section"
              bodyClassName="p-4"
            >
              {timeSeries.length === 0 ? (
                <div
                  className="text-[12px] py-6 text-center"
                  style={{ color: "var(--ds-color-ink-muted)" }}
                >
                  No redemptions in this range yet.
                </div>
              ) : (
                <div
                  style={{ width: "100%", height: 240 }}
                  data-testid="admin-coupons-analytics-timeseries"
                >
                  <ResponsiveContainer>
                    <RCLineChart data={timeSeries} margin={{ top: 8, right: 16, left: 4, bottom: 8 }}>
                      <CartesianGrid
                        stroke="var(--ds-color-admin-border)"
                        vertical={false}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "var(--ds-color-ink-subtle)", fontSize: 10 }}
                        axisLine={{ stroke: "var(--ds-color-admin-border)" }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: "var(--ds-color-ink-subtle)", fontSize: 10 }}
                        axisLine={{ stroke: "var(--ds-color-admin-border)" }}
                        tickLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "var(--ds-color-surface)",
                          border: "1px solid var(--ds-color-admin-border)",
                          borderRadius: "var(--ds-radius-input)",
                          fontSize: 12,
                          color: "var(--ds-color-ink)",
                        }}
                        labelStyle={{ color: "var(--ds-color-ink)", fontWeight: 600 }}
                        itemStyle={{ color: "var(--ds-color-ink-muted)" }}
                        cursor={{ stroke: "var(--ds-color-admin-border)" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="redemptions"
                        stroke="var(--ds-color-accent-strong)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </RCLineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Section>

            <Section
              title="Per-coupon performance"
              icon={Ticket}
              testid="admin-coupons-analytics-table-section"
              bodyClassName="p-0"
            >
              <div className="overflow-x-auto">
                <table
                  className="w-full text-[13px]"
                  data-testid="admin-coupons-analytics-table"
                >
                  <thead>
                    <tr
                      className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                      style={{
                        color: "var(--ds-color-ink-subtle)",
                        background: "var(--ds-color-admin-surface-muted)",
                        borderBottom: "1px solid var(--ds-color-admin-border)",
                      }}
                    >
                      <th className="px-4 py-2">Code</th>
                      <th className="px-4 py-2">Discount</th>
                      <th className="px-4 py-2 text-right">Redemptions ({range})</th>
                      <th className="px-4 py-2 text-right">Redemptions (total)</th>
                      <th className="px-4 py-2 text-right">Revenue impact</th>
                      <th className="px-4 py-2 text-right">Active subs</th>
                      <th className="px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perCoupon.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-8 text-center"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          No coupons match the current filter.
                        </td>
                      </tr>
                    ) : (
                      perCoupon.map((r, idx) => {
                        const isLast = idx === perCoupon.length - 1;
                        return (
                          <tr
                            key={r.code}
                            data-testid={`admin-coupons-analytics-row-${r.code}`}
                            style={{
                              borderBottom: isLast
                                ? "none"
                                : "1px solid var(--ds-color-admin-border)",
                            }}
                          >
                            <td
                              className="px-4 py-1.5 font-mono"
                              style={{ color: "var(--ds-color-ink)" }}
                            >
                              {r.code}
                            </td>
                            <td
                              className="px-4 py-1.5 tabular-nums"
                              style={{ color: "var(--ds-color-ink-muted)" }}
                            >
                              {r.discount_type === "percentage"
                                ? `${r.discount_value}% off`
                                : `${fmtDollars(r.discount_value)} off`}
                            </td>
                            <td
                              className="px-4 py-1.5 text-right tabular-nums font-mono"
                              style={{ color: "var(--ds-color-ink)" }}
                            >
                              {r.redemptions_in_range}
                            </td>
                            <td
                              className="px-4 py-1.5 text-right tabular-nums font-mono"
                              style={{ color: "var(--ds-color-ink-muted)" }}
                            >
                              {r.redemptions_total}
                              {r.max_redemptions ? ` / ${r.max_redemptions}` : ""}
                            </td>
                            <td
                              className="px-4 py-1.5 text-right tabular-nums font-mono"
                              style={{ color: "var(--ds-color-ink)" }}
                            >
                              {fmtDollars(r.revenue_impact_cents)}
                            </td>
                            <td
                              className="px-4 py-1.5 text-right tabular-nums"
                              style={{ color: "var(--ds-color-ink-muted)" }}
                            >
                              {r.active_subscribers}
                            </td>
                            <td className="px-4 py-1.5">
                              <StatusChip status={r.current_status} />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
